import type { ConflictRecovery, SaveFailure, SaveFailureKind, SaveFeedback, SaveRecovery, SaveRecoveryAction, SaveStatus } from 'elowen-plugin-ui-kit';
import { isUnconfirmedWrite } from './elowenClient';

// The public feedback types have ONE owner, the plugin UI kit; this module re-exports them and owns what the
// types cannot say: which recoveries each kind needs, how an error is classified, how feedback folds.
export type { ConflictRecovery, SaveFailure, SaveFailureKind, SaveFeedback, SaveRecoveryAction } from 'elowen-plugin-ui-kit';

function hasRecovery(recovery: SaveRecovery, name: keyof SaveRecovery): boolean {
  return typeof recovery[name] === 'function';
}

/** The contract of a failure, checked where it is reported. A type cannot protect a plugin bundle, and an
 *  expiring card with no way out is exactly the failure the seam exists to prevent, so a violation throws. */
export function assertFailureContract(failure: SaveFailure): void {
  const { kind, recovery, owner } = failure;
  if (kind === 'conflict' && !hasRecovery(recovery, 'reload') && !hasRecovery(recovery, 'merge')) {
    throw new Error(`A conflict must offer reload or merge, and none was supplied (owner ${owner}).`);
  }
  if (kind === 'unconfirmed') {
    if (hasRecovery(recovery, 'reload') === hasRecovery(recovery, 'leave')) {
      throw new Error(`An unconfirmed write must offer exactly one of reload (a real re-read) or leave (abandonment that verifies nothing) (owner ${owner}).`);
    }
  } else if (hasRecovery(recovery, 'leave')) {
    throw new Error(`Only an unconfirmed write may be left: a ${kind} failure has its own recoveries (owner ${owner}).`);
  }
  if ((kind === 'conflict' || kind === 'unconfirmed') && hasRecovery(recovery, 'retry')) {
    throw new Error(`A ${kind} write is never retried blindly: remove retry (owner ${owner}).`);
  }
  if ((kind === 'operation' || kind === 'validation') && (hasRecovery(recovery, 'reload') || hasRecovery(recovery, 'merge'))) {
    throw new Error(`A ${kind} failure has no reload or merge: report it as a conflict or an unconfirmed write (owner ${owner}).`);
  }
  if (kind === 'validation' && hasRecovery(recovery, 'retry')) {
    throw new Error(`A rejected value is corrected at its field, never retried (owner ${owner}).`);
  }
}

/** The feedback shape, checked at every seam that reads it: all members are required and a halted status
 *  must carry the failure that explains it. */
export function assertSaveFeedback(feedback: SaveFeedback): void {
  if (typeof feedback?.status !== 'string' || typeof feedback.editing !== 'boolean'
    || typeof feedback.attempt !== 'number' || !Array.isArray(feedback.failures)) {
    throw new Error('Save feedback needs status, editing, attempt and failures; pass a useAutoSaveStatus result or combineSaveFeedback.');
  }
  if ((feedback.status === 'error' || feedback.status === 'unconfirmed') && feedback.failures.length === 0) {
    throw new Error(`Save feedback is ${feedback.status} but carries no failure to report.`);
  }
  for (const failure of feedback.failures) assertFailureContract(failure);
}

/** A write refused because it lost agreement with the current state (a stale selection, a changed revision,
 * a scope that moved), thrown by an operation a surface awaits. It is reported as a conflict with exactly the
 * recoveries it carries and is never a success. */
export class SaveConflictError extends Error {
  constructor(readonly recovery: ConflictRecovery, readonly userMessage?: string) {
    super(userMessage ?? 'Save conflict');
    this.name = 'SaveConflictError';
    if (typeof recovery.reload !== 'function' && typeof recovery.merge !== 'function') {
      throw new TypeError('A SaveConflictError needs reload or merge.');
    }
  }
}

export type FailureContext = {
  /** Only an operation proven not applied may be retried; the owner says how. */
  retry?: SaveRecoveryAction;
  /** Curated text for an `operation` failure. */
  message?: string;
  /** The owner's own reading of its rejections (controllers only). */
  classify?: (error: unknown) => { kind: SaveFailureKind; message?: string; recovery?: { reload?: SaveRecoveryAction; merge?: SaveRecoveryAction } };
} & (
  /** The owner's authoritative re-read: the recovery of a conflict without its own and of a lost connection. */
  | { reload: SaveRecoveryAction; leave?: never }
  /** An awaited operation with no re-read to offer (a confirmation the domain gave none): a lost connection can
   *  only be abandoned, which verifies nothing and is labelled so. */
  | { leave: SaveRecoveryAction; reload?: never }
);

/** THE classifier: the only place that turns a rejection into a failure kind and its recoveries, for save
 *  controllers and for operations a dialog awaits alike. A lost connection is unconfirmed, a `SaveConflictError`
 *  a conflict with the recoveries it carries, anything else what the owner's `classify` says or an `operation`. */
export function classifyFailure(error: unknown, context: FailureContext): Pick<SaveFailure, 'kind' | 'message' | 'recovery'> {
  if (isUnconfirmedWrite(error)) return { kind: 'unconfirmed', recovery: context.reload ? { reload: context.reload } : { leave: context.leave } };
  if (error instanceof SaveConflictError) return { kind: 'conflict', message: error.userMessage, recovery: error.recovery } as Pick<SaveFailure, 'kind' | 'message' | 'recovery'>;
  const classified = context.classify?.(error) ?? { kind: 'operation' as const };
  const message = classified.message ?? (classified.kind === 'operation' ? context.message : undefined);
  switch (classified.kind) {
    case 'validation': return { kind: 'validation', message, recovery: {} };
    case 'operation': return { kind: 'operation', message, recovery: context.retry ? { retry: context.retry } : {} };
    case 'conflict': return { kind: 'conflict', message, recovery: { reload: classified.recovery?.reload ?? context.reload, merge: classified.recovery?.merge } } as Pick<SaveFailure, 'kind' | 'message' | 'recovery'>;
    case 'unconfirmed': return { kind: 'unconfirmed', message, recovery: classified.recovery?.reload ?? context.reload ? { reload: classified.recovery?.reload ?? context.reload } : { leave: context.leave } } as Pick<SaveFailure, 'kind' | 'message' | 'recovery'>;
  }
}

/** Whether the rejection of an awaited answer (a confirmation, a selection save) forbids giving the same answer
 *  again before a recovery: a conflict (the answer no longer fits) or an unconfirmed outcome (it may already have
 *  been applied). Only an `operation` proven not applied stays retryable. Read through {@link classifyFailure}, so
 *  the card and the lock can never disagree. */
export function holdsAnswer(error: unknown): boolean {
  return classifyFailure(error, { leave: () => {} }).kind !== 'operation';
}

/** A recovery that reports completion: `released` runs only after `recover` resolved, so a recovery that rejects
 *  leaves whatever it was holding held. */
export function releasing(recover: SaveRecoveryAction, released: () => void): SaveRecoveryAction {
  return async () => { await recover(); released(); };
}

/** The rejection with the recoveries it carries wrapped by {@link releasing}; any other rejection is returned
 *  unchanged (its recoveries come from the surface that reports it). */
export function releasedBy(error: unknown, released: () => void): unknown {
  if (!(error instanceof SaveConflictError)) return error;
  const { reload, merge } = error.recovery;
  return new SaveConflictError({
    ...(reload ? { reload: releasing(reload, released) } : {}),
    ...(merge ? { merge: releasing(merge, released) } : {}),
  } as ConflictRecovery, error.userMessage);
}

/** Fold several sections' save states into one, as a pure function of its children. Errors win, then
 * unconfirmed writes, active saves, delayed activation, saved, and finally idle. Child failures stay
 * distinct and keep their own typed recovery; the attempt is the sum of the children's, so it moves whenever
 * any member settles a write and never for a no-op. */
export function combineSaveFeedback(...items: SaveFeedback[]): SaveFeedback {
  for (const item of items) assertSaveFeedback(item);
  const base = {
    editing: items.some((item) => item.editing),
    attempt: items.reduce((sum, item) => sum + item.attempt, 0),
    failures: items.flatMap((item) => [...item.failures]),
  };
  const status: SaveStatus = items.some((item) => item.status === 'error') ? 'error'
    : items.some((item) => item.status === 'unconfirmed') ? 'unconfirmed'
      : items.some((item) => item.status === 'saving') ? 'saving'
        : items.some((item) => item.status === 'pending') ? 'pending'
          : items.some((item) => item.status === 'saved') ? 'saved' : 'idle';
  return { ...base, status };
}
